import { type File, Memo } from '@kalos/kalos-rpc';
import {
  Button,
  Card,
  CardContent,
  DataTable,
  DataTableColumnHeader,
  DataTablePagination,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Form,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kalos/ui';
import {
  DownloadIcon,
  EyeOpenIcon,
  Pencil1Icon,
  TrashIcon,
  UploadIcon,
} from '@radix-ui/react-icons';
import {
  type ColumnDef,
  getCoreRowModel,
  type OnChangeFn,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { format, parseISO } from 'date-fns';
import { Loader2, Plus, Sparkles } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { DialogOrSheet } from '../../../../../components/DialogOrSheet';
import { LoadErrorState } from '../../../../../components/LoadErrorState';
import { getLoadingColumns } from '../../../../../components/utils';
import {
  FORM_AUTOMATIC_SEARCH_DEBOUNCE_TIME,
  NOTE_REPORT_LIMIT,
  ROWS_PER_PAGE,
} from '../../../../../constants';
import { useAuth } from '../../../../../context/AuthContext';
import {
  useDeleteMemoMutation,
  useMemoClassificationQuery,
  useMemoQuery,
  useMemoStatusQuery,
  useUpdateMemoMutation,
} from '../../../../../hooks/react-query/useMemoQuery';
import { useBatchUserQuery } from '../../../../../hooks/react-query/useUserQuery';
import { debounce } from '../../../../../tools/helpers';
import { MemoDetailSheet } from '../../../Memo/components/MemoDetailSheet';
import { MemoExport } from '../../../Memo/components/MemoExport';
import { MemoReportForm } from '../../../Memo/components/MemoReportDialog';
import { MemoSummaryList } from '../../../Memo/components/MemoSummaryList';
import {
  MEMO_FILE_TYPE_COLORS,
  MEMO_NOTES_CATEGORY_PILL_CLASS,
  memoStatusPillClass,
} from '../../../Memo/memoTableColumnUtils';
import { Modal } from '../../../Modal';
import { OmniGallery } from '../../../OmniGallery';
import { EditMemoForm } from '../NotesForm/EditMemoForm';
import { AddJobNoteModal } from './AddJobNoteModal';
import { AddMemoFileForm } from './AddMemoFileForm';
import { JobNoteFilters } from './JobNoteFilters';
import {
  defaultJobNoteFilterValues,
  jobNoteFilterDimensions,
  type JobNoteFilterValues,
} from './jobNoteFiltersUtils';
import { buildJobNoteMemoFilters, type MemoListOrderDir } from './jobNoteMemoRequest';
import { NoteComposer } from './NoteComposer';
import { NotesFeed } from './NotesFeed';

const CREATED_DATE_COLUMN_SORT_ID = 'created_date';

function memoListOrderFromSorting(sorting: SortingState): {
  orderBy: string;
  orderDir: MemoListOrderDir;
} {
  const first = sorting[0];
  if (first?.id === CREATED_DATE_COLUMN_SORT_ID) {
    return { orderBy: 'created_date', orderDir: first.desc ? 'DESC' : 'ASC' };
  }
  return { orderBy: 'created_date', orderDir: 'DESC' };
}

const staticNotes = Array.from({ length: 5 }, (_, i) => Memo.create({ id: i }));

interface JobNoteListProps {
  jobId?: number;
  projectPrimaryEventId?: number;
  /** When set (e.g. project tab), runs after a note is created instead of the modal default toast */
  onAddNoteSuccess?: () => void | Promise<void>;
  hideSummary?: boolean;
  showExport?: boolean;
  /**
   * Phone layout (NotesFeed): the note composer, then the notes as a feed with a
   * filter sheet. Callers that don't pass it are unchanged.
   */
  mobileLayout?: boolean;
  /** Phone layout: opens a note as its own page. */
  onOpenNote?: (noteId: number) => void;
}

export const JobNoteList = ({
  jobId,
  projectPrimaryEventId,
  onAddNoteSuccess,
  hideSummary = false,
  showExport = false,
  mobileLayout = false,
  onOpenNote,
}: JobNoteListProps) => {
  const [selectedNote, setSelectedNote] = useState<Memo | null>(null);
  const [showFilesModal, setShowFilesModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [submissionLogTab, setSubmissionLogTab] = useState('Notes');
  const [isReportDialogOpen, setIsReportDialogOpen] = useState(false);
  const [isAddNoteModalOpen, setIsAddNoteModalOpen] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailSheet, setShowDetailSheet] = useState(false);
  const [currentNoteIndex, setCurrentNoteIndex] = useState(-1);
  const [isEditingMemo, setIsEditingMemo] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [isLoadingNextPage, setIsLoadingNextPage] = useState(false);
  const [sheetCurrentPage, setSheetCurrentPage] = useState(0);
  const [navigationDirection, setNavigationDirection] = useState<'next' | 'prev' | null>(null);
  const [sorting, setSorting] = useState<SortingState>([
    { id: CREATED_DATE_COLUMN_SORT_ID, desc: true },
  ]);
  const [filters, setFilters] = useState<JobNoteFilterValues>(defaultJobNoteFilterValues);

  // Create form in parent - this is the key change
  const filterForm = useForm<JobNoteFilterValues>({
    defaultValues: defaultJobNoteFilterValues,
  });

  // Set up debounced filter change in parent
  useEffect(() => {
    const debouncedUpdate = debounce((data: JobNoteFilterValues) => {
      setCurrentPage(0);
      setFilters(data);
    }, FORM_AUTOMATIC_SEARCH_DEBOUNCE_TIME);

    const subscription = filterForm.watch(debouncedUpdate);
    return () => subscription.unsubscribe();
  }, [filterForm]);

  const handleResetFilters = useCallback(() => {
    filterForm.reset(defaultJobNoteFilterValues);
    setFilters(defaultJobNoteFilterValues);
  }, [filterForm]);

  const memoSummaryParentDateRange = useMemo(
    () => ({
      startDate: filters.startDate ? format(filters.startDate, 'yyyy-MM-dd') : undefined,
      endDate: filters.endDate ? format(filters.endDate, 'yyyy-MM-dd') : undefined,
    }),
    [filters.startDate, filters.endDate],
  );

  const { user: loggedUser } = useAuth();
  const isAdmin = useMemo(
    () => loggedUser.permissionGroups.some((perm) => perm.name === 'Manager'),
    [loggedUser],
  );

  const isCanEditTranscript = useMemo(
    () => loggedUser.permissionGroups.some((perm) => perm.name === 'CanEditTranscript'),
    [loggedUser],
  );

  const canEditMemoStatus = useMemo(
    () =>
      loggedUser.permissionGroups.some(
        (perm) => perm.name === 'MemoAdmin' || perm.name === 'Manager',
      ),
    [loggedUser],
  );

  // Query to get all note creators for this job (for filter dropdown)
  const noteCreatorsQuery = useMemoQuery({
    filters: {
      ...(projectPrimaryEventId ? { projectPrimaryEventId } : { eventId: jobId }),
      isActive: true,
      withoutLimit: true,
      includeFiles: false,
    },
    enabled: !!(jobId || projectPrimaryEventId),
  });

  // Get unique creator IDs
  const noteCreatorIds = useMemo(() => {
    if (!noteCreatorsQuery.data?.results) return [];
    const uniqueIds = new Set(
      noteCreatorsQuery.data.results.map((note) => note.createdBy).filter((id) => id > 0),
    );
    return Array.from(uniqueIds);
  }, [noteCreatorsQuery.data?.results]);

  // Get unique event IDs (for project context job filter)
  const noteEventIds = useMemo(() => {
    if (!noteCreatorsQuery.data?.results) return [];
    const uniqueIds = new Set(
      noteCreatorsQuery.data.results.map((note) => note.eventId).filter((id) => id > 0),
    );
    return Array.from(uniqueIds).sort((a, b) => a - b);
  }, [noteCreatorsQuery.data?.results]);

  const memoListOrder = useMemo(() => memoListOrderFromSorting(sorting), [sorting]);

  const handleMemoSortingChange = useCallback<OnChangeFn<SortingState>>((updater) => {
    setSorting(updater);
    setCurrentPage(0);
  }, []);

  const noteQuery = useMemoQuery({
    enabled: !!(jobId || projectPrimaryEventId),
    filters: buildJobNoteMemoFilters({
      filters,
      jobId,
      projectPrimaryEventId,
      pageNumber: currentPage,
      withoutLimit: false,
      orderBy: memoListOrder.orderBy,
      orderDir: memoListOrder.orderDir,
    }),
  });

  const { mutateAsync: deleteMemo } = useDeleteMemoMutation();
  const { mutateAsync: updateMemo, isPending: isUpdatingMemo } = useUpdateMemoMutation();

  // Queries for detail sheet
  const userBatchGetQuery = useBatchUserQuery({
    filters: {
      isEmployee: 1,
      overrideLimit: true,
    },
  });
  const memoStatusQuery = useMemoStatusQuery();
  const memoClassificationQuery = useMemoClassificationQuery();

  const handleUploadFiles = useCallback((note: Memo) => {
    setSelectedNote(note);
    setShowUploadModal(true);
  }, []);

  const handleDeleteNote = useCallback(async () => {
    if (!selectedNote) return;

    try {
      await deleteMemo(selectedNote);
      setShowDeleteModal(false);
      setSelectedNote(null);
      toast({
        title: 'Note deleted',
        description: 'The note has been successfully deleted',
      });
    } catch (error) {
      console.error('Error deleting note:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete the note',
        variant: 'destructive',
      });
    }
  }, [selectedNote, deleteMemo]);

  const closeFilesModal = useCallback(() => {
    setShowFilesModal(false);
    setSelectedNote(null);
  }, []);

  const closeUploadModal = useCallback(() => {
    setShowUploadModal(false);
    setSelectedNote(null);
  }, []);

  const handleEditNote = useCallback((note: Memo) => {
    setSelectedNote(note);
    setShowEditModal(true);
  }, []);

  const handleEditSuccess = useCallback(() => {
    setShowEditModal(false);
    setSelectedNote(null);
  }, []);

  const closeEditModal = useCallback(() => {
    setShowEditModal(false);
    setSelectedNote(null);
  }, []);

  const handleViewDetails = useCallback(
    (note: Memo, index: number) => {
      setSelectedNote(note);
      setCurrentNoteIndex(index);
      setSheetCurrentPage(currentPage);
      setShowDetailSheet(true);
    },
    [currentPage],
  );

  const closeDetailSheet = useCallback(() => {
    setShowDetailSheet(false);
    setSelectedNote(null);
    setCurrentNoteIndex(-1);
    setIsEditingMemo(false);
    setIsLoadingNextPage(false);
    setNavigationDirection(null);
  }, []);

  const getFileTypes = useCallback((files: File[] | undefined) => {
    if (!files || files.length === 0) return { hasAudio: false, hasPhoto: false, hasVideo: false };
    let hasAudio = false;
    let hasPhoto = false;
    let hasVideo = false;

    files.forEach((file) => {
      if (file.mimeType.includes('audio')) {
        hasAudio = true;
      }
      if (file.mimeType.includes('image') || file.mimeType.includes('pdf')) {
        hasPhoto = true;
      }
      if (file.mimeType.includes('video')) {
        hasVideo = true;
      }
    });

    return { hasAudio, hasPhoto, hasVideo };
  }, []);

  const handleSaveMemoInSheet = useCallback(
    async (updatedMemo?: Memo) => {
      const memoReq = updatedMemo
        ? Memo.clone(updatedMemo)
        : selectedNote
          ? Memo.clone(selectedNote)
          : null;

      if (!memoReq || !memoReq.fieldMask || memoReq.fieldMask.length === 0) {
        setIsEditingMemo(false);
        return;
      }

      try {
        await updateMemo(memoReq);

        // Update the selected note with the new data
        if (updatedMemo) {
          setSelectedNote(updatedMemo);
        }

        toast({
          title: 'Note updated',
          description: 'Your changes have been saved.',
        });
        setIsEditingMemo(false);
      } catch (err) {
        console.error('Error saving memo:', err);
        toast({
          title: 'Error',
          description: 'Failed to save changes',
          variant: 'destructive',
        });
      }
    },
    [selectedNote, updateMemo],
  );

  const handleSetState = useCallback((partialState: { selectedMemo: Memo }) => {
    setSelectedNote(partialState.selectedMemo);
  }, []);

  // Add effect to handle modal state changes
  useEffect(() => {
    const handleResize = () => {
      if (!selectedNote?.files?.results?.length) {
        setShowFilesModal(false);
        setSelectedNote(null);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [selectedNote?.files?.results?.length]);

  // Pagination constants and calculations - use server totalCount
  const displayNotes = useMemo(() => {
    const notes = noteQuery.data?.results || [];
    return [...notes].sort((a, b) => {
      if (a.isPinned && !b.isPinned) return -1;
      if (!a.isPinned && b.isPinned) return 1;
      return 0;
    });
  }, [noteQuery.data?.results]);

  const hasComments = useCallback(
    (memo: Memo) => Boolean(memo.commentCount && memo.commentCount > 0),
    [],
  );

  const notesColumns = useMemo<ColumnDef<Memo>[]>(
    () => [
      {
        id: 'row_number',
        header: ({ column }) => (
          <DataTableColumnHeader hideVisibilityToggle title="#" column={column} />
        ),
        meta: { className: 'w-12 text-center' },
        enableSorting: false,
        cell({ row, column }) {
          const memo = row.original;
          const rowNumber = currentPage * ROWS_PER_PAGE + row.index + 1;
          const fileTypes = getFileTypes(memo.files?.results);
          const hasCommentsValue = hasComments(memo);
          return (
            <div className={column.columnDef.meta?.className}>
              <div className="flex flex-col items-center gap-1">
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-200 text-xs font-medium">
                  {rowNumber}
                </div>
                <div className="flex gap-1">
                  {fileTypes.hasAudio && (
                    <span
                      style={{
                        backgroundColor: MEMO_FILE_TYPE_COLORS.audio,
                        height: '8px',
                        width: '8px',
                        borderRadius: '50%',
                        display: 'inline-block',
                      }}
                    />
                  )}
                  {fileTypes.hasPhoto && (
                    <span
                      style={{
                        backgroundColor: MEMO_FILE_TYPE_COLORS.photo,
                        height: '8px',
                        width: '8px',
                        borderRadius: '50%',
                        display: 'inline-block',
                      }}
                    />
                  )}
                  {fileTypes.hasVideo && (
                    <span
                      style={{
                        backgroundColor: MEMO_FILE_TYPE_COLORS.video,
                        height: '8px',
                        width: '8px',
                        borderRadius: '50%',
                        display: 'inline-block',
                      }}
                    />
                  )}
                  {hasCommentsValue && (
                    <span
                      style={{
                        backgroundColor: MEMO_FILE_TYPE_COLORS.comment,
                        height: '8px',
                        width: '8px',
                        borderRadius: '50%',
                        display: 'inline-block',
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'description',
        enableSorting: false,
        meta: { className: 'w-32' },
        header: ({ column }) => (
          <DataTableColumnHeader hideVisibilityToggle title="Description" column={column} />
        ),
        cell({ row, column }) {
          const fullDescription = row.original.description;
          if (!fullDescription || fullDescription.trim() === '') {
            return <div className={column.columnDef.meta?.className}>-</div>;
          }
          return (
            <div
              role="button"
              tabIndex={0}
              title={fullDescription}
              className={`${column.columnDef.meta?.className} -m-2 cursor-pointer break-words p-2 transition-colors hover:bg-[#f0f6ff] md:-mx-3 md:-my-4 md:px-3 md:py-4`}
              onClick={(e) => {
                e.stopPropagation();
                const idx = displayNotes.findIndex((n) => n.id === row.original.id);
                handleViewDetails(row.original, idx >= 0 ? idx : row.index);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  const idx = displayNotes.findIndex((n) => n.id === row.original.id);
                  handleViewDetails(row.original, idx >= 0 ? idx : row.index);
                }
              }}
            >
              <span className="line-clamp-2 text-blue-600">{fullDescription}</span>
            </div>
          );
        },
      },
      {
        accessorKey: 'classificationId',
        id: 'classification_id',
        header: ({ column }) => (
          <DataTableColumnHeader hideVisibilityToggle title="Category" column={column} />
        ),
        meta: { className: 'w-24' },
        enableSorting: false,
        cell({ row, column }) {
          const category =
            row.original.classificationId === 0
              ? 'No Category'
              : memoClassificationQuery.data?.results.find(
                  (c) => c.id === row.original.classificationId,
                )?.categoryDescription;
          return (
            <div className={column.columnDef.meta?.className}>
              <span className={MEMO_NOTES_CATEGORY_PILL_CLASS}>{category}</span>
            </div>
          );
        },
      },
      {
        accessorKey: 'created_by',
        id: 'created_by',
        enableSorting: false,
        meta: { className: 'w-24' },
        header: ({ column }) => (
          <DataTableColumnHeader hideVisibilityToggle title="Created By" column={column} />
        ),
        cell({ row, column }) {
          return (
            <div className={column.columnDef.meta?.className}>{row.original.createdByName}</div>
          );
        },
      },
      {
        accessorKey: 'createdDate',
        id: 'created_date',
        header: ({ column }) => (
          <DataTableColumnHeader hideVisibilityToggle title="Created Date" column={column} />
        ),
        meta: { className: 'w-20 text-xs' },
        cell({ row, column }) {
          const raw = row.original.createdDate;
          if (!raw) return <span className="text-xs text-gray-500">-</span>;
          const date = parseISO(raw);
          if (isNaN(date.getTime())) return <span className="text-xs text-gray-500">-</span>;
          return (
            <div className={column.columnDef.meta?.className}>
              <div className="flex flex-col">
                <span className="text-sm font-medium">{format(date, 'MM/dd/yyyy')}</span>
                <span className="text-xs text-gray-600">{format(date, 'hh:mm a')}</span>
              </div>
            </div>
          );
        },
      },
      {
        id: 'department',
        accessorKey: 'userDepartmentName',
        header: ({ column }) => (
          <DataTableColumnHeader hideVisibilityToggle title="Department" column={column} />
        ),
        enableSorting: false,
        meta: { className: 'w-24 text-sm' },
        cell({ row, column }) {
          return (
            <div className={column.columnDef.meta?.className}>
              {row.original.userDepartmentName || 'No Department'}
            </div>
          );
        },
      },
      {
        id: 'status_id',
        accessorKey: 'statusId',
        enableSorting: false,
        header: ({ column }) => (
          <DataTableColumnHeader hideVisibilityToggle title="Status" column={column} />
        ),
        cell({ row }) {
          const status =
            row.original.statusId === 0
              ? 'No Status'
              : memoStatusQuery.data?.results.find((s) => s.id === row.original.statusId)
                  ?.statusDescription;

          return (
            <div className="w-24">
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${memoStatusPillClass(row.original.statusId)}`}
              >
                {status}
              </span>
            </div>
          );
        },
      },
      {
        id: 'actions',
        header: ({ column }) => (
          <DataTableColumnHeader hideVisibilityToggle title="Actions" column={column} />
        ),
        meta: { className: 'min-w-[11rem]' },
        cell({ row, column }) {
          const note = row.original;
          const canDelete = isAdmin || note.createdBy === loggedUser.id;
          const noteIndex = displayNotes.findIndex((n) => n.id === note.id);
          return (
            <div className={column.columnDef.meta?.className}>
              <TooltipProvider delayDuration={150}>
                <div className="flex flex-nowrap items-center gap-1">
                  {isAdmin && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-gray-600 hover:bg-gray-200 hover:text-gray-900"
                          onClick={() =>
                            handleViewDetails(note, noteIndex >= 0 ? noteIndex : row.index)
                          }
                        >
                          <EyeOpenIcon className="h-3.5 w-3.5" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>View Details (Admin)</TooltipContent>
                    </Tooltip>
                  )}
                  {canDelete && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-gray-600 hover:bg-gray-200 hover:text-gray-900"
                          onClick={() => handleEditNote(note)}
                        >
                          <Pencil1Icon className="h-3.5 w-3.5" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Edit Note</TooltipContent>
                    </Tooltip>
                  )}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-gray-600 hover:bg-gray-200 hover:text-gray-900"
                        onClick={() => handleUploadFiles(note)}
                      >
                        <UploadIcon className="h-3.5 w-3.5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Upload Files</TooltipContent>
                  </Tooltip>
                  {canDelete && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-red-600 hover:bg-red-100 hover:text-red-700"
                          onClick={() => {
                            setSelectedNote(note);
                            setShowDeleteModal(true);
                          }}
                        >
                          <TrashIcon className="h-3.5 w-3.5" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Delete Note</TooltipContent>
                    </Tooltip>
                  )}
                </div>
              </TooltipProvider>
            </div>
          );
        },
      },
    ],
    [
      currentPage,
      getFileTypes,
      hasComments,
      displayNotes,
      handleViewDetails,
      handleEditNote,
      handleUploadFiles,
      isAdmin,
      loggedUser.id,
      memoClassificationQuery.data?.results,
      memoStatusQuery.data?.results,
    ],
  );

  const loadingColumns = useMemo(() => getLoadingColumns(notesColumns), [notesColumns]);

  const notesTable = useReactTable({
    data: noteQuery.isPending ? staticNotes : displayNotes,
    getCoreRowModel: getCoreRowModel(),
    columns: noteQuery.isPending ? loadingColumns : notesColumns,
    manualSorting: true,
    onSortingChange: handleMemoSortingChange,
    state: {
      sorting,
      pagination: {
        pageIndex: 0,
        pageSize: ROWS_PER_PAGE,
      },
    },
  });

  const totalNotes = noteQuery.data?.totalCount || 0;

  const [isViewingSavedReport, setIsViewingSavedReport] = useState(false);

  const noteFormJobId = useMemo(() => {
    if (jobId != null && jobId > 0) return jobId;
    if (projectPrimaryEventId != null && projectPrimaryEventId > 0) {
      return filters.eventId > 0 ? filters.eventId : projectPrimaryEventId;
    }
    return undefined;
  }, [jobId, projectPrimaryEventId, filters.eventId]);

  const reportMemoRequest = useMemo(
    () =>
      Memo.create(
        buildJobNoteMemoFilters({
          filters,
          jobId,
          projectPrimaryEventId,
          pageNumber: 0,
          withoutLimit: true,
          orderBy: memoListOrder.orderBy,
          orderDir: memoListOrder.orderDir,
        }),
      ),
    [filters, jobId, projectPrimaryEventId, memoListOrder.orderBy, memoListOrder.orderDir],
  );

  const isGenerateReportDisabled =
    noteQuery.isPending || totalNotes === 0 || totalNotes > NOTE_REPORT_LIMIT;

  const pageCount = Math.ceil(totalNotes / ROWS_PER_PAGE);
  const shouldShowPagination = totalNotes > ROWS_PER_PAGE;

  // Clamp currentPage when it falls out of bounds (e.g. after deleting the last
  // item on the last page). This follows the standard pagination best practice of
  // navigating to the nearest valid page rather than showing an empty page.
  useEffect(() => {
    if (!noteQuery.isPending && pageCount > 0 && currentPage >= pageCount) {
      setCurrentPage(pageCount - 1);
    }
  }, [currentPage, pageCount, noteQuery.isPending]);

  useEffect(() => {
    if (!isLoadingNextPage) {
      setSheetCurrentPage(currentPage);
    }
  }, [currentPage, isLoadingNextPage]);

  useEffect(() => {
    if (
      !isLoadingNextPage ||
      !navigationDirection ||
      noteQuery.isFetching ||
      !displayNotes.length
    ) {
      return;
    }

    const targetMemo =
      navigationDirection === 'next' ? displayNotes[0] : displayNotes[displayNotes.length - 1];
    const targetIndex = navigationDirection === 'next' ? 0 : displayNotes.length - 1;

    setSelectedNote(targetMemo);
    setCurrentNoteIndex(targetIndex);
    setIsEditingMemo(false);
    setIsLoadingNextPage(false);
    setNavigationDirection(null);
  }, [displayNotes, isLoadingNextPage, navigationDirection, noteQuery.isFetching]);

  const navigateToNote = useCallback(
    (direction: 'next' | 'prev') => {
      if (!displayNotes.length || !noteQuery.data) return;

      const currentIndex = currentNoteIndex;
      const page = sheetCurrentPage;
      const totalCount = noteQuery.data.totalCount || 0;
      const pageSize = ROWS_PER_PAGE;

      let newIndex: number;
      let newPage: number;
      let shouldLoadNewPage = false;

      if (direction === 'next') {
        if (currentIndex < displayNotes.length - 1) {
          newIndex = currentIndex + 1;
          newPage = page;
        } else {
          const currentGlobalIndex = page * pageSize + currentIndex;
          if (currentGlobalIndex < totalCount - 1) {
            newPage = page + 1;
            newIndex = 0;
            shouldLoadNewPage = true;
          } else {
            return;
          }
        }
      } else {
        if (currentIndex > 0) {
          newIndex = currentIndex - 1;
          newPage = page;
        } else if (page > 0) {
          newPage = page - 1;
          newIndex = pageSize - 1;
          shouldLoadNewPage = true;
        } else {
          return;
        }
      }

      if (shouldLoadNewPage) {
        setIsLoadingNextPage(true);
        setNavigationDirection(direction);
        setCurrentPage(newPage);
      } else {
        const newNote = displayNotes[newIndex];
        setSelectedNote(newNote);
        setCurrentNoteIndex(newIndex);
        setIsEditingMemo(false);
      }
    },
    [currentNoteIndex, displayNotes, noteQuery.data, sheetCurrentPage],
  );

  const canNavigateNext = useMemo(() => {
    if (!noteQuery.data?.results) return false;

    const pageSize = ROWS_PER_PAGE;
    const currentGlobalIndex = sheetCurrentPage * pageSize + currentNoteIndex;
    const totalCount = noteQuery.data.totalCount || 0;
    return currentGlobalIndex < totalCount - 1;
  }, [currentNoteIndex, noteQuery.data?.results, noteQuery.data?.totalCount, sheetCurrentPage]);

  const canNavigatePrev = useMemo(() => {
    return sheetCurrentPage > 0 || currentNoteIndex > 0;
  }, [currentNoteIndex, sheetCurrentPage]);

  if (noteQuery.isError) {
    return mobileLayout ? (
      <LoadErrorState
        title="Couldn't load notes"
        description={`Error loading notes: ${noteQuery.error.message}`}
        retryLabel="Retry loading notes"
        onRetry={() => void noteQuery.refetch()}
        isRetrying={noteQuery.isFetching}
      />
    ) : (
      <div className="p-4 text-red-500">Error loading notes: {noteQuery.error.message}</div>
    );
  }

  const reportDialogContent = (
    <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
      <DialogHeader>
        <DialogTitle>Generate AI Report</DialogTitle>
      </DialogHeader>
      <MemoReportForm
        memoRequest={reportMemoRequest}
        enabled={isReportDialogOpen}
        onClose={() => setIsReportDialogOpen(false)}
      />
    </DialogContent>
  );

  // Dialogs and sheets for a selected note; both layouts use them.
  const noteModals = (
    <>
      {selectedNote && showFilesModal && (
        <Modal open={showFilesModal} onClose={closeFilesModal}>
          <OmniGallery
            files={selectedNote.files?.results || []}
            title={selectedNote.description}
            readOnly={false}
            allowDelete={true}
            memoId={selectedNote.id}
          />
        </Modal>
      )}

      {selectedNote && showUploadModal && (
        <AddMemoFileForm
          memoId={selectedNote.id}
          isOpen={showUploadModal}
          onClose={closeUploadModal}
        />
      )}

      {selectedNote && showDeleteModal && (
        <DialogOrSheet
          open={showDeleteModal}
          onOpenChange={setShowDeleteModal}
          title="Delete Note"
          description="Are you sure you want to delete this note? This action cannot be undone."
        >
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteNote}>
              Delete
            </Button>
          </div>
        </DialogOrSheet>
      )}

      {selectedNote && showEditModal && (
        <DialogOrSheet
          open={showEditModal}
          onOpenChange={(open) => !open && closeEditModal()}
          title="Edit Note"
          description="Update the note text and adjust pin or internal settings."
          dialogClassName="max-h-[90vh] max-w-[800px] overflow-y-auto"
        >
          <EditMemoForm
            memo={selectedNote}
            onClose={closeEditModal}
            onSuccess={handleEditSuccess}
          />
        </DialogOrSheet>
      )}

      {selectedNote && showDetailSheet && (
        <MemoDetailSheet
          open={showDetailSheet}
          onOpenChange={closeDetailSheet}
          selectedMemo={selectedNote}
          memoFiles={selectedNote.files?.results || []}
          currentMemoIndex={currentNoteIndex}
          sheetCurrentPage={sheetCurrentPage}
          memoTableLength={totalNotes}
          isLoadingNextPage={isLoadingNextPage}
          canNavigateNext={canNavigateNext}
          canNavigatePrev={canNavigatePrev}
          isCanEditTranscript={isCanEditTranscript}
          navigateToMemo={navigateToNote}
          getFileTypes={getFileTypes}
          onEditMemo={() => setIsEditingMemo(true)}
          userBatchGetQuery={userBatchGetQuery}
          memoStatusQuery={memoStatusQuery}
          memoClassificationQuery={memoClassificationQuery}
          isEditingMemo={isEditingMemo}
          setIsEditingMemo={setIsEditingMemo}
          handleSaveMemoInSheet={handleSaveMemoInSheet}
          isUpdatingMemo={isUpdatingMemo}
          handleSetState={handleSetState}
          canEditMemoStatus={canEditMemoStatus}
        />
      )}
    </>
  );

  if (mobileLayout) {
    return (
      <>
        <NotesFeed
          notes={displayNotes}
          isLoading={noteQuery.isPending}
          isFetching={noteQuery.isFetching}
          isError={noteQuery.isError}
          onRetry={() => void noteQuery.refetch()}
          totalNotes={totalNotes}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          statuses={memoStatusQuery.data?.results}
          onOpenNote={(note) => onOpenNote?.(note.id)}
          emptyDescription="Notes added to this project show here."
          composer={
            noteFormJobId ? (
              <NoteComposer
                jobId={noteFormJobId}
                loggedUserId={loggedUser.id}
                categories={memoClassificationQuery.data?.results ?? []}
                onSaved={onAddNoteSuccess}
              />
            ) : (
              <p className="text-muted-foreground text-sm">
                Choose a job in the filters to add a note to it.
              </p>
            )
          }
          filterForm={filterForm}
          searchField="searchText"
          filterDimensions={jobNoteFilterDimensions}
          defaultFilterValues={defaultJobNoteFilterValues}
          renderFilterFields={() => (
            <JobNoteFilters
              inSheet
              availableCreatorIds={noteCreatorIds}
              availableEventIds={noteEventIds}
              showEventIdFilter={!!projectPrimaryEventId}
              // The sheet's own Reset filters control resets the staged fields.
              onReset={() => undefined}
            />
          )}
          onClearFilters={handleResetFilters}
          canGenerateReport={!!(jobId || projectPrimaryEventId) && !isGenerateReportDisabled}
          generateReportDisabledReason={
            totalNotes > NOTE_REPORT_LIMIT
              ? `Too many notes for one report. Narrow your filters (current filter: ${totalNotes} notes).`
              : totalNotes === 0
                ? 'No notes match the current filters.'
                : null
          }
          onGenerateReport={() => setIsReportDialogOpen(true)}
          isViewingReport={isViewingSavedReport}
          reports={
            <MemoSummaryList
              mobileLayout
              onReportOpenChange={setIsViewingSavedReport}
              datesControlledExternally={true}
              primaryEventId={projectPrimaryEventId}
              parentDateRange={memoSummaryParentDateRange}
            />
          }
        />
        <DialogOrSheet
          open={isReportDialogOpen}
          onOpenChange={setIsReportDialogOpen}
          title="Generate AI Report"
        >
          <MemoReportForm
            memoRequest={reportMemoRequest}
            enabled={isReportDialogOpen}
            onClose={() => setIsReportDialogOpen(false)}
          />
        </DialogOrSheet>
        {noteModals}
      </>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filter Component - wrapped in Form context */}
      <Form {...filterForm}>
        <JobNoteFilters
          availableCreatorIds={noteCreatorIds}
          availableEventIds={noteEventIds}
          showEventIdFilter={!!projectPrimaryEventId}
          onReset={handleResetFilters}
        />
      </Form>

      <Tabs value={submissionLogTab} onValueChange={setSubmissionLogTab}>
        <div className="text-card-foreground mb-4 flex w-full flex-wrap items-center justify-between gap-4 rounded-xl border bg-white p-4 shadow-sm">
          <TabsList className="bg-muted/40 flex h-max w-fit flex-wrap justify-start">
            <TabsTrigger
              value="Notes"
              className="!rounded-sm first:!rounded-l-sm last:!rounded-r-sm"
            >
              Notes
            </TabsTrigger>
            <TabsTrigger
              value="Reports"
              className="!rounded-sm first:!rounded-l-sm last:!rounded-r-sm"
            >
              Reports
            </TabsTrigger>
          </TabsList>
          {(jobId || projectPrimaryEventId) && (
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              <Dialog open={isReportDialogOpen} onOpenChange={setIsReportDialogOpen}>
                <div className="flex shrink-0 items-center gap-2">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipContent>
                        {totalNotes > NOTE_REPORT_LIMIT ? (
                          <>
                            <p>Too many notes.</p>
                            <p>
                              This report is too large to generate in one go. Please narrow your
                              filters (smaller date range or fewer notes) and try again.
                            </p>
                            <p className="mt-1 text-xs">Current filter: {totalNotes} notes</p>
                          </>
                        ) : totalNotes === 0 ? (
                          <>
                            <p>No notes match the current filters.</p>
                            <p>Adjust filters or add notes to generate an AI report.</p>
                          </>
                        ) : (
                          <>
                            <p>AI report</p>
                            <p>
                              Generates a report from the notes currently shown in the list (same
                              filters).
                            </p>
                          </>
                        )}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      className="gap-2 border-transparent bg-gradient-to-r from-[#ef4444] to-[#ec4899] text-white shadow-sm hover:border-transparent hover:bg-gradient-to-r hover:from-[#ef4444] hover:to-[#ec4899] hover:text-white"
                      disabled={isGenerateReportDisabled}
                    >
                      <Sparkles className="h-4 w-4 shrink-0" />
                      Generate Report
                    </Button>
                  </DialogTrigger>
                </div>
                {reportDialogContent}
              </Dialog>
              <Button
                type="button"
                variant="outline"
                className="gap-2 border-transparent bg-gradient-to-r from-[#ef4444] to-[#ec4899] text-white shadow-sm hover:border-transparent hover:bg-gradient-to-r hover:from-[#ef4444] hover:to-[#ec4899] hover:text-white"
                disabled={!noteFormJobId}
                onClick={() => setIsAddNoteModalOpen(true)}
              >
                <Plus className="h-4 w-4 shrink-0" />
                Add Note
              </Button>
              <AddJobNoteModal
                open={isAddNoteModalOpen}
                onOpenChange={setIsAddNoteModalOpen}
                loggedUserId={loggedUser.id}
                jobId={noteFormJobId}
                onSuccess={onAddNoteSuccess}
              />
            </div>
          )}
        </div>

        <Card className="mt-4 bg-white shadow-sm">
          <CardContent className="p-4">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-xl font-semibold">Submission Log</h2>
              {submissionLogTab === 'Notes' && (
                <div className="flex flex-wrap items-center gap-4">
                  {showExport && jobId && (
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="outline" className="gap-2">
                          <DownloadIcon />
                          Export
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle>Export Job Notes</DialogTitle>
                        </DialogHeader>
                        <MemoExport
                          showFilterForm
                          initialFormValues={{
                            eventId: jobId,
                            startDate: filters.startDate
                              ? format(filters.startDate, 'yyyy-MM-dd')
                              : undefined,
                            endDate: filters.endDate
                              ? format(filters.endDate, 'yyyy-MM-dd')
                              : undefined,
                          }}
                          lockedFields={{ eventId: true }}
                        />
                      </DialogContent>
                    </Dialog>
                  )}
                  {shouldShowPagination && (
                    <DataTablePagination
                      currentPage={currentPage}
                      setPage={setCurrentPage}
                      pageCount={pageCount}
                      disabled={noteQuery.isPending || noteQuery.isFetching}
                    />
                  )}
                  <span className="text-sm text-gray-600">
                    Total: {totalNotes} {totalNotes === 1 ? 'note' : 'notes'}
                  </span>
                </div>
              )}
            </div>
            <TabsContent value="Notes" className="mt-0 focus-visible:outline-none">
              <div className="relative">
                {noteQuery.isFetching && !noteQuery.isPending && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 backdrop-blur-sm">
                    <div className="flex items-center gap-2 rounded-lg bg-white px-4 py-3 shadow-lg">
                      <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                      <span className="text-sm font-medium text-gray-700">Loading...</span>
                    </div>
                  </div>
                )}
                {!noteQuery.isPending && displayNotes.length === 0 ? (
                  <div className="py-4 text-center text-gray-500">
                    No notes match the selected filters
                  </div>
                ) : (
                  <div className="flex-1 overflow-auto bg-white [&_thead]:bg-gray-50 [&_thead_th>div]:text-[14px] [&_thead_th]:text-[14px] [&_thead_th]:font-medium [&_thead_th]:text-black [&_thead_th_button]:text-[14px] [&_thead_th_button]:text-black">
                    <DataTable table={notesTable} />
                  </div>
                )}
              </div>
            </TabsContent>
            <TabsContent value="Reports" className="mt-0 focus-visible:outline-none">
              <MemoSummaryList
                datesControlledExternally={true}
                primaryEventId={projectPrimaryEventId}
                parentDateRange={memoSummaryParentDateRange}
              />
            </TabsContent>
          </CardContent>
        </Card>
      </Tabs>

      {noteModals}
    </div>
  );
};
